import React, { useState } from 'react';
import legacyUapiService from '../../services/LegacyUapiService';

// Choose which of a module's published operations to create on the route.
//
// The alternative is typing method+path for every call a module exposes — 23 for act.json, over 200 for
// rsv.json — which is how a route tree comes to describe endpoints the API does not have and to miss
// ones it does. The spec already states them; this transcribes the chosen ones.
//
// Operations the tree already holds are shown but not selectable: the unique key on (route, method,
// path) would reject them one at a time, and an unticked row that says "already here" reads better than
// an error after the fact.
export default function UapiOperationImport({ preview, routePath, onCancel, onImported }) {
    const importable = (preview.operations || []).filter(o => !o.exists);
    const [picked, setPicked] = useState(importable.map(o => `${o.method} ${o.path}`));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const key = (o) => `${o.method} ${o.path}`;
    const toggle = (k) => setPicked(p => (p.includes(k) ? p.filter(x => x !== k) : [...p, k]));

    const run = async () => {
        if (!picked.length) return;
        setBusy(true);
        setError(null);
        try {
            const chosen = importable.filter(o => picked.includes(key(o)))
                .map(o => ({ method: o.method, path: o.path }));
            const res = await legacyUapiService.importSpecOperations(
                preview.routeId, preview.specFile, chosen);
            const d = res.data || res;
            onImported(d);
        } catch (e) {
            setError(e.response?.data?.error?.message || e.response?.data?.message || e.message
                || 'Import failed');
            setBusy(false);
        }
    };

    const allPicked = picked.length === importable.length && importable.length > 0;

    return (
        <div className="lu-import-backdrop" onClick={() => !busy && onCancel()}>
            <div className="lu-import-modal" onClick={(e) => e.stopPropagation()}>
                <div className="lu-import-head">
                    <h3 className="lu-import-title">
                        Import operations for {routePath} from {preview.specFile}
                    </h3>
                    <p className="lu-import-sub">
                        {preview.specTitle || 'The spec'}{preview.specVersion ? ` ${preview.specVersion}` : ''} publishes{' '}
                        {preview.operations.length} operation{preview.operations.length === 1 ? '' : 's'};{' '}
                        {preview.operations.length - importable.length} already on this route. Paths are shown
                        relative to {routePath}. Nothing has been written yet.
                    </p>
                    {importable.length > 0 && (
                        <label className="lu-import-all">
                            <input
                                type="checkbox"
                                checked={allPicked}
                                ref={(el) => { if (el) el.indeterminate = picked.length > 0 && !allPicked; }}
                                onChange={() => setPicked(allPicked ? [] : importable.map(key))}
                                disabled={busy}
                            />
                            Select all ({importable.length})
                        </label>
                    )}
                </div>

                <div className="lu-import-list">
                    {preview.operations.map((o) => (
                        <label
                            key={key(o)}
                            className={`lu-import-row${picked.includes(key(o)) ? ' picked' : ''}${o.exists ? ' lu-import-row-held' : ''}`}
                        >
                            <input
                                type="checkbox"
                                checked={o.exists || picked.includes(key(o))}
                                onChange={() => toggle(key(o))}
                                disabled={busy || o.exists}
                            />
                            <span className="lu-import-row-main">
                                <span className="lu-import-op">
                                    <span className={`lu-method lu-method-${o.method.toLowerCase()}`}>{o.method}</span>
                                    {' '}{o.path}
                                    {o.exists && <span className="lu-import-already">on this route</span>}
                                    {/* Oracle marks withdrawn calls; importing one is usually a mistake,
                                        so it is labelled rather than hidden. */}
                                    {o.deprecated && <span className="lu-import-deprecated">deprecated</span>}
                                </span>
                                {o.description && <span className="lu-import-summary">{o.description}</span>}
                            </span>
                        </label>
                    ))}
                </div>

                {error && <p className="ppw-error-msg" style={{ margin: '0 1.5rem' }}>{error}</p>}

                <div className="lu-import-footer">
                    <button type="button" className="pp-criteria-add-btn" onClick={onCancel} disabled={busy}>
                        Cancel
                    </button>
                    <button type="button" className="pp-rules-add-btn" onClick={run} disabled={busy || !picked.length}>
                        {busy ? 'Importing…' : `Import ${picked.length} operation${picked.length === 1 ? '' : 's'}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
